import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Button } from '../../components/ui/button';
import { CheckCircle, Users, Clock, TrendingUp, ArrowRight, Briefcase } from 'lucide-react';

const TradeManagement = () => {
  return (
    <div className="service-page">
      <Header />
      
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-block h-1 w-16 bg-slate-600 mb-8"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Trade Management<br />Services
            </h1>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-8">
              Expert trade compliance support without adding headcount. We function as your off-site trade compliance department, providing sophisticated expertise at a fraction of the cost of full-time personnel.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-slate-700 hover:bg-slate-800 text-white px-10 h-14">
                Explore Trade Management Solutions
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Cost-Effective Compliance Without Compromise
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Trade Management provides companies with access to seasoned trade compliance professionals without the overhead, benefits, and training costs associated with full-time employees. Our team becomes an extension of your organization, handling day-to-day compliance operations while you focus on core business activities.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                This innovative approach delivers enterprise-grade trade compliance capabilities to organizations of any size, from emerging exporters to established multinational corporations.
              </p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Cost Comparison</h3>
              <div className="space-y-4">
                <div className="border-b border-slate-200 pb-4">
                  <p className="text-sm text-gray-600 mb-2">Full-Time Trade Compliance Manager</p>
                  <p className="text-3xl font-bold text-gray-900">$120K - $180K</p>
                  <p className="text-sm text-gray-600 mt-1">+ benefits, training, systems, overhead</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-2">Trade Management Services</p>
                  <p className="text-3xl font-bold text-slate-700">$15K - $60K</p>
                  <p className="text-sm text-gray-600 mt-1">Scalable, flexible, immediate expertise</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12">
            Comprehensive Trade Compliance Operations
          </h2>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <Briefcase className="h-10 w-10 text-slate-700 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Daily Compliance Operations</h3>
              <ul className="space-y-3">
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Export transaction screening and authorization</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">License application preparation and tracking</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Classification determinations (ECCN, USML, HTS)</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Denied party screening integration and monitoring</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Shipping documentation review and approval</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Recordkeeping and audit trail maintenance</span>
                </li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <Users className="h-10 w-10 text-slate-700 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Strategic Compliance Support</h3>
              <ul className="space-y-3">
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Compliance program development and enhancement</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Internal policies and procedures documentation</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Regulatory change monitoring and impact assessment</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Employee training program delivery</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Internal audit coordination and response</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Government audit support and representation</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Technology Integration & Systems</h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Seamless integration with your existing business systems including ERP platforms, trade management software, and shipping systems. We work within your technology ecosystem or can recommend and implement best-in-class compliance solutions.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">System Integration</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• ERP system connectivity</li>
                  <li>• Trade management platforms</li>
                  <li>• Screening software configuration</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Data Management</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Product database maintenance</li>
                  <li>• Customer/vendor screening lists</li>
                  <li>• License tracking systems</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Reporting & Analytics</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Compliance metrics dashboards</li>
                  <li>• Transaction reporting</li>
                  <li>• Risk assessment analytics</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
            Why Trade Management Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="h-8 w-8 text-slate-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Immediate Expertise</h3>
              <p className="text-gray-600">
                Access experienced trade compliance professionals immediately, without months of recruiting and onboarding. Start operations with fully trained experts from day one.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-8 w-8 text-slate-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Scalable Resources</h3>
              <p className="text-gray-600">
                Scale support up or down based on business volume and complexity. No fixed overhead costs or underutilized personnel during slower periods.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Briefcase className="h-8 w-8 text-slate-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Depth of Knowledge</h3>
              <p className="text-gray-600">
                Benefit from collective expertise across multiple compliance professionals and industry verticals, not limited to a single employee's experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Transform Your Trade Compliance Operations
          </h2>
          <p className="text-base sm:text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            Discover how Trade Management can elevate your compliance capabilities while reducing costs.
          </p>
          <a href="https://calendly.com/nancy-wood-blackbird/0tg-trade-compliance-services" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="bg-white hover:bg-slate-100 text-slate-900 px-10 h-14 font-semibold">
              Schedule Trade Management Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TradeManagement;
